'use strict'; // OP-only checkout. Native payment handlers and other package routes are untouched.

const P = require('./protocol.js');

const METHODS = Object.freeze({
  alipay: 'CNY',
  wechat: 'CNY',
  visa: 'HKD'
});
const STATES = new Set(['INITIATING', 'PENDING', 'CONFIRMED', 'CANCELLED', 'FAILED', 'REVIEW_REQUIRED']);
const TERMINAL = new Set(['CONFIRMED', 'CANCELLED', 'FAILED']);

const fail = code => {
  throw Object.assign(Error(code), {
    code
  });
};

const need = (ok, code) => {
  if (!ok) fail(code);
};

const plain = x => x && typeof x === 'object' && !Array.isArray(x);

const clone = x => JSON.parse(JSON.stringify(x));

const units = x => typeof x === 'string' && /^[1-9][0-9]{0,14}$/.test(x);

const identifier = x => P.id(x);

const safeInt = x => Number.isSafeInteger(x) && x >= 0;

function selection(value) {
  need(plain(value) && Object.keys(value).every(k => ['skuId', 'activityId'].includes(k)) && /^(permanent-(2|3|5|10)|limited-step-[1-6])$/.test(value.skuId || ''), 'INVALID_CHECKOUT_SELECTION');
  const limited = value.skuId.startsWith('limited-');
  need(limited ? identifier(value.activityId) : value.activityId === undefined || value.activityId === null, 'INVALID_CHECKOUT_ACTIVITY');
  return Object.freeze({
    skuId: value.skuId,
    ...(limited ? {
      activityId: value.activityId
    } : {})
  });
}

function isCardPaymentRoute(search) {
  return new URLSearchParams(search || '').getAll('activity').includes('opcard');
}

function parsePaymentRoute(search) {
  const query = new URLSearchParams(search || '');
  need(query.getAll('activity').length === 1 && query.get('activity') === 'opcard' && [...query.keys()].every(k => ['activity', 'skuId', 'activityId', 'checkoutId'].includes(k)), 'INVALID_CARD_PAYMENT_ROUTE');

  for (const key of query.keys()) need(query.getAll(key).length === 1, 'INVALID_CARD_PAYMENT_ROUTE');

  const selected = selection({
    skuId: query.get('skuId'),
    ...(query.has('activityId') ? {
      activityId: query.get('activityId')
    } : {})
  });
  const checkoutId = query.get('checkoutId');
  need(checkoutId === null || identifier(checkoutId), 'INVALID_CHECKOUT_ID');
  return { ...selected,
    checkoutId
  };
}

function paymentPath(value, checkoutId) {
  const selected = selection(value),
        q = new URLSearchParams({
    activity: 'opcard',
    ...selected
  });

  if (checkoutId !== undefined && checkoutId !== null) {
    need(identifier(checkoutId), 'INVALID_CHECKOUT_ID');
    q.set('checkoutId', checkoutId);
  }

  return '/payment-providers/?' + q.toString();
}

function counts(value, selected) {
  var _value$activityId, _selected$activityId;

  need(value.skuId === selected.skuId && ((_value$activityId = value.activityId) !== null && _value$activityId !== void 0 ? _value$activityId : null) === ((_selected$activityId = selected.activityId) !== null && _selected$activityId !== void 0 ? _selected$activityId : null) && safeInt(value.paidPacks) && value.paidPacks > 0 && safeInt(value.giftPacks) && value.paidPacks + value.giftPacks <= 22 && String(value.goldbase) === String(value.paidPacks * 100), 'CHECKOUT_PACKAGE_MISMATCH');
  const normal = {
    'permanent-2': [2, 0],
    'permanent-3': [3, 0],
    'permanent-5': [5, 0],
    'permanent-10': [10, 0]
  };
  const steps = [[2, 0], [2, 0], [3, 1], [3, 0], [4, 0], [5, 2]];
  const expected = normal[selected.skuId] || steps[Number(selected.skuId.slice(-1)) - 1];
  need(expected && value.paidPacks === expected[0] && value.giftPacks === expected[1], 'CHECKOUT_PACK_COUNT_CHANGED');
}

function offers(value, selected, now = Date.now()) {
  selected = selection(selected);
  need(plain(value) && value.schema === 1, 'INVALID_CHECKOUT_OFFERS');
  counts(value, selected);
  need(value.totalPacks === value.paidPacks + value.giftPacks && value.immediatePaidGold === 0 && value.immediateFreeGold === 0 && Array.isArray(value.offers) && value.offers.length <= 3, 'INVALID_CHECKOUT_OFFERS');
  const seen = new Set();

  for (const offer of value.offers) {
    need(plain(offer) && Object.prototype.hasOwnProperty.call(METHODS, offer.paymentMethod) && !seen.has(offer.paymentMethod) && offer.currency === METHODS[offer.paymentMethod] && units(offer.amountMinor) && identifier(offer.offerId) && Number.isSafeInteger(offer.expiresAt) && offer.expiresAt > now, 'INVALID_CHECKOUT_OFFER');
    seen.add(offer.paymentMethod);
  }

  return P.frozen(clone(value));
}

function paymentAction(action, status, scopeKey) {
  if (action === null) return null;
  need(plain(action) && status === 'PENDING' && Object.keys(action).sort().join(',') === 'type,url' && ['REDIRECT', 'QR'].includes(action.type) && typeof action.url === 'string' && action.url.length <= 4096, 'INVALID_PAYMENT_ACTION');
  let url;

  try {
    url = new URL(action.url);
  } catch {
    fail('INVALID_PAYMENT_ACTION');
  } // Server freezes the provider destination. Never render HTML, forms or scripts.


  // Approved native HTTP parity is limited to the authenticated SDK owner scope.
  // scopeKey comes from read-client identity, never the checkout URL or query string.
  const nativeHttp = scopeKey === 'opcard:c2d6730b-fff8-4195-b098-953af42acced:0c24fbec-7a13-465b-8c61-345081844786' && url.protocol === 'http:' && ['http://123.129.224.147:89', 'http://m.hd123.asia', 'http://www.gxhuizhongyuan.cn'].includes(url.origin) && !/[\s\\]/.test(action.url);
  need(!url.username && !url.password && !url.hash && (nativeHttp || (url.protocol === 'https:' && url.port === '' && /^[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i.test(url.hostname) && !/^\d+(?:\.\d+){3}$/.test(url.hostname) && !/\.(?:localhost|local|internal)$/i.test(url.hostname))), 'INVALID_PAYMENT_ACTION');
  if (url.hostname === 'api.apluspay.io') need(/^\/payments\/web\/pay\/order[A-Za-z0-9_-]{1,128}$/.test(url.pathname) && !url.search, 'INVALID_PAYMENT_ACTION');
  return {
    type: action.type,
    url: url.href
  };
}

function checkout(value, selected, expectedOffer, scopeKey) {
  selected = selection(selected);
  need(plain(value) && value.schema === 1 && identifier(value.checkoutId) && STATES.has(value.status), 'INVALID_CHECKOUT');

  if (value.status === 'INITIATING' && value.quote === null) {
    need(value.payment === null && (value.packIds === undefined || Array.isArray(value.packIds) && value.packIds.length === 0), 'INVALID_INITIATING_CHECKOUT');
    return P.frozen(clone(value));
  }

  need(plain(value.quote), 'INVALID_CHECKOUT_QUOTE');
  const q = value.quote;
  counts(q, selected);
  need(identifier(q.quoteId) && ['CNY', 'HKD'].includes(q.currency) && units(q.amountMinor) && Number.isSafeInteger(q.expiresAt), 'INVALID_CHECKOUT_QUOTE');
  if (expectedOffer) need(q.currency === expectedOffer.currency && q.amountMinor === expectedOffer.amountMinor, 'CHECKOUT_PRICE_CHANGED');
  need(Object.prototype.hasOwnProperty.call(value, 'payment'), 'MISSING_PAYMENT_ACTION');
  paymentAction(value.payment, value.status, scopeKey);
  if (value.status === 'CONFIRMED') need(value.payment === null && Array.isArray(value.packIds) && value.packIds.length === q.paidPacks + q.giftPacks && new Set(value.packIds).size === value.packIds.length && value.packIds.every(id => /^cardpack_[a-f0-9]{64}$/.test(id)), 'PAYMENT_NOT_CONFIRMED');else need(value.packIds === undefined || Array.isArray(value.packIds) && value.packIds.length === 0, 'PREMATURE_PACK_IDS');
  return P.frozen(clone(value));
}

function createCheckoutSession(host, scopeKey) {
  try {
    if (!identifier(scopeKey) || !(host === null || host === void 0 ? void 0 : host.crypto) || typeof host.crypto.getRandomValues !== 'function') return null;
    const raw = host.sessionStorage,
          key = 'op-card-checkout-intents-v1:' + scopeKey;
    if (!raw || typeof raw.getItem !== 'function' || typeof raw.setItem !== 'function' || typeof raw.removeItem !== 'function') return null;

    const makeRequestId = () => {
      const bytes = new Uint8Array(16);
      host.crypto.getRandomValues(bytes);
      return 'opcheckout-' + Array.from(bytes, n => n.toString(16).padStart(2, '0')).join('');
    };

    const probe = key + ':probe:' + makeRequestId();
    raw.setItem(probe, '1');
    const ok = raw.getItem(probe) === '1';
    raw.removeItem(probe);
    if (!ok) return null;

    const check = k => {
      need(k === key, 'CHECKOUT_STORAGE_SCOPE');
      return k;
    };

    return Object.freeze({
      storage: Object.freeze({
        getItem: k => raw.getItem(check(k)),
        setItem: (k, v) => raw.setItem(check(k), String(v)),
        removeItem: k => raw.removeItem(check(k))
      }),
      makeRequestId
    });
  } catch {
    return null;
  }
}

function createCommerce({
  request,
  storage,
  scopeKey,
  makeRequestId,
  navigate,
  now = Date.now
}) {
  need(typeof request === 'function' && storage && identifier(scopeKey) && typeof makeRequestId === 'function' && typeof navigate === 'function', 'COMMERCE_DEPENDENCIES_REQUIRED');
  const storeKey = 'op-card-checkout-intents-v1:' + scopeKey;
  let disposed = false;
  const inFlight = new Map();

  const alive = () => need(!disposed, 'SESSION_CHANGED');

  const keyFor = s => s.skuId + ':' + (s.activityId || '');

  function load() {
    alive();
    const raw = storage.getItem(storeKey);
    if (!raw) return {};
    let v;

    try {
      v = JSON.parse(raw);
    } catch {
      fail('INVALID_CHECKOUT_INTENTS');
    }

    need(plain(v) && v.schema === 1 && plain(v.items) && Object.keys(v.items).length <= 64, 'INVALID_CHECKOUT_INTENTS');

    for (const [key, item] of Object.entries(v.items)) {
      need(plain(item) && keyFor(selection(item.selection)) === key && identifier(item.idempotencyKey) && identifier(item.offerId) && Object.prototype.hasOwnProperty.call(METHODS, item.paymentMethod) && (item.checkoutId === null || identifier(item.checkoutId)), 'INVALID_CHECKOUT_INTENT');
    }

    return v.items;
  }

  function save(items) {
    alive();
    need(Object.keys(items).length <= 64, 'CHECKOUT_STORAGE_FULL');
    const raw = JSON.stringify({
      schema: 1,
      items
    });
    storage.setItem(storeKey, raw);
    need(storage.getItem(storeKey) === raw, 'CHECKOUT_INTENT_NOT_PERSISTED');
  }

  function intent(value) {
    const s = selection(value);
    return clone(load()[keyFor(s)] || null);
  }

  async function getOffers(value) {
    alive();
    const s = selection(value);
    const query = new URLSearchParams(s);
    const result = await request({
      method: 'GET',
      path: '/op-cards/checkout-offers?' + query
    });
    alive();
    return offers(result, s, now());
  }

  async function getCheckout(id, value) {
    alive();
    const s = selection(value);
    need(identifier(id), 'INVALID_CHECKOUT_ID');
    const result = await request({
      method: 'GET',
      path: '/op-cards/checkouts/' + id
    });
    alive();
    const v = checkout(result, s, undefined, scopeKey);
    need(v.checkoutId === id, 'CHECKOUT_ID_CHANGED');
    return v;
  }

  async function resumeCheckout(id, value) {
    alive();
    const s = selection(value);
    need(identifier(id), 'INVALID_CHECKOUT_ID');
    const result = await request({
      method: 'POST',
      path: '/op-cards/checkouts/' + id + '/resume',
      body: {}
    });
    alive();
    const v = checkout(result, s, undefined, scopeKey);
    need(v.checkoutId === id, 'CHECKOUT_ID_CHANGED');
    return v;
  }

  async function start(value, offer) {
    alive();
    const s = selection(value),
          key = keyFor(s);
    if (inFlight.has(key)) return inFlight.get(key);

    const job = (async () => {
      var _latest$key2;

      const items = load();
      let stored = items[key];

      if (stored) {
        if (stored.checkoutId) {
          const prior = await getCheckout(stored.checkoutId, s);
          if (prior.status !== 'INITIATING') return prior;
        }

        if (offer) need(stored.paymentMethod === offer.paymentMethod, 'CHECKOUT_ALREADY_PENDING');
      } else {
        need(offer && Object.prototype.hasOwnProperty.call(METHODS, offer.paymentMethod) && offer.currency === METHODS[offer.paymentMethod] && identifier(offer.offerId) && units(offer.amountMinor) && Number.isSafeInteger(offer.expiresAt) && offer.expiresAt > now(), 'OFFER_EXPIRED');
        stored = {
          selection: s,
          paymentMethod: offer.paymentMethod,
          offerId: offer.offerId,
          idempotencyKey: makeRequestId(),
          checkoutId: null
        };
        need(identifier(stored.idempotencyKey), 'INVALID_CHECKOUT_IDEMPOTENCY_KEY');
        items[key] = stored;
        save(items);
      }

      const body = { ...s,
        paymentMethod: stored.paymentMethod,
        offerId: stored.offerId,
        idempotencyKey: stored.idempotencyKey
      };
      let response,
          recovered = false;

      try {
        response = await request({
          method: 'POST',
          path: '/op-cards/checkouts',
          body
        });
      } catch (error) {
        alive();

        if (error.status === 409 && error.code === 'CHECKOUT_ALREADY_PENDING' && identifier(error.checkoutId)) {
          response = await getCheckout(error.checkoutId, s);
          recovered = true;
        } else {
          if (error.status === 409 && ['OFFER_EXPIRED', 'OFFER_CHANGED'].includes(error.code) && error.checkoutCreated === false) {
            var _latest$key;

            const latest = load();

            if (((_latest$key = latest[key]) === null || _latest$key === void 0 ? void 0 : _latest$key.idempotencyKey) === stored.idempotencyKey) {
              delete latest[key];
              save(latest);
            }
          }

          throw error;
        }
      }

      alive();
      const result = checkout(response, s, !recovered && offer && offer.offerId === stored.offerId ? offer : null, scopeKey);
      const latest = load();
      need(((_latest$key2 = latest[key]) === null || _latest$key2 === void 0 ? void 0 : _latest$key2.idempotencyKey) === stored.idempotencyKey, 'CHECKOUT_INTENT_CHANGED');
      latest[key] = { ...stored,
        checkoutId: result.checkoutId
      };
      save(latest);
      return result;
    })();

    inFlight.set(key, job);

    try {
      return await job;
    } finally {
      if (inFlight.get(key) === job) inFlight.delete(key);
    }
  }

  async function clearFinished(value) {
    var _latest$keyFor;

    const s = selection(value),
          items = load(),
          stored = items[keyFor(s)];
    if (!stored) return;
    need(stored.checkoutId, 'CHECKOUT_OUTCOME_UNKNOWN');
    const result = await getCheckout(stored.checkoutId, s);
    need(TERMINAL.has(result.status), 'CHECKOUT_STILL_PENDING');
    const latest = load();
    need(((_latest$keyFor = latest[keyFor(s)]) === null || _latest$keyFor === void 0 ? void 0 : _latest$keyFor.idempotencyKey) === stored.idempotencyKey, 'CHECKOUT_INTENT_CHANGED');
    delete latest[keyFor(s)];
    save(latest);
    return result;
  }

  return Object.freeze({
    offers: getOffers,
    getCheckout,
    resumeCheckout,
    start,
    intent,
    clearFinished,
    navigateSelection: value => {
      alive();
      navigate(paymentPath(value));
    },

    dispose() {
      disposed = true;
    }

  });
}

module.exports = {
  METHODS,
  STATES,
  TERMINAL,
  selection,
  isCardPaymentRoute,
  parsePaymentRoute,
  paymentPath,
  offers,
  checkout,
  paymentAction,
  createCheckoutSession,
  createCommerce
};
